import { ConfigProvider, Table, TableProps } from 'antd';
import React, { FC, ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { HeightStyle } from 'components/atoms/input/input';
import { TableSkeleton } from 'components/atoms/table';
import { ProductiveHeading07 } from 'components/styles';

export const StyledTable = styled(Table)`
  td.phase {
    display: flex;
  }

  .ant-table-thead > tr > th {
    ${ProductiveHeading07};
    color: var(--gray-80);
    background: var(--coolest-gray-20);
    font-size: var(--font-size-base);
  }
  .ant-table-tbody > tr > td {
    font-weight: var(----heading-5-weight);
    font-size: var(--font-size-base);
    height: ${HeightStyle};
  }
  .ant-table-cell-fix-left {
    border-right: 1px solid var(--border-color);
  }
  .ant-table-placeholder > .ant-table-cell {
    border: 0;
  }
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--white-100);
  padding: 0 var(--spacing-05) var(--spacing-05);
`;

const Title = styled.h1`
  ${ProductiveHeading07};
  letter-spacing: 0.16px;
  color: var(--black-100);
  font-weight: var(--font-weight-bold) !important;
  padding: var(--spacing-04);
  font-size: var(--heading-6-size);
  padding-left: 0;
`;

interface Props<T = any> extends TableProps<T> {
  titleLabel?: string;
  tableBodyHeight?: number;
  isLoading?: boolean;
  renderEmpty: () => ReactNode;
}

export const TemplateInsightBaseTable: FC<Props> = ({
  dataSource,
  columns: propColumns,
  titleLabel,
  tableBodyHeight,
  isLoading = false,
  renderEmpty,
}) => {
  const columns = useMemo(() => {
    if (!propColumns) {
      return propColumns;
    }
    const [first, ...rest] = propColumns;

    return [
      {
        ...first,
        width: first.width || '35ch',
        fixed: true,
        align: 'left' as const,
      },
      ...rest.map((col) => ({
        ...col,
        width: col.width || '25ch',
        align: 'left' as const,
      })),
    ];
  }, [propColumns]);

  if (isLoading) {
    return <TableSkeleton columnCount={8} />;
  }

  return (
    <ConfigProvider renderEmpty={renderEmpty}>
      <TableWrapper>
        {titleLabel && <Title>{titleLabel}</Title>}
        <StyledTable
          dataSource={dataSource}
          columns={columns}
          scroll={
            tableBodyHeight
              ? {
                  y: `calc( ${tableBodyHeight}px - var(--spacing-05))`,
                }
              : undefined
          }
          pagination={false}
          size="small"
        />
      </TableWrapper>
    </ConfigProvider>
  );
};
